/**
 * EventsBrowserWidget — Browse events across sports
 * Single sport dropdown with auto-load on selection.
 * API: GET /events/:sport (FREE) — one sport at a time
 * CSS prefix: ebw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchOddsSports, fetchOddsEvents } from '../lib/api'
import { getSportIcon } from '../lib/oddsConstants'
import { Calendar, RefreshCw, ChevronRight, ChevronDown, Check, Copy, Search, Filter } from 'lucide-react'
import TeamLogo from './shared/TeamLogo'
import './EventsBrowserWidget.css'

function EventsBrowserWidget({ onGetEventOdds, initialSport }) {
  const [allSports, setAllSports] = useState([])
  const [selectedSport, setSelectedSport] = useState(initialSport || '')
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [sportsLoading, setSportsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [expandedDates, setExpandedDates] = useState(new Set())
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showDateFilters, setShowDateFilters] = useState(false)
  const [lastFetch, setLastFetch] = useState(null)
  const [copyFeedback, setCopyFeedback] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  // Load sports list
  useEffect(() => {
    async function loadSports() {
      setSportsLoading(true)
      const res = await fetchOddsSports()
      if (res.status === 'ok' && res.data) {
        setAllSports(res.data.filter(s => s.active))
      }
      setSportsLoading(false)
    }
    loadSports()
  }, [])

  // Group sports by category for optgroups
  const sportsByGroup = {}
  allSports.forEach(s => {
    const group = s.group || 'Other'
    if (!sportsByGroup[group]) sportsByGroup[group] = []
    sportsByGroup[group].push(s)
  })

  // Fetch events for the selected sport
  const fetchData = useCallback(async (sportKey) => {
    if (!sportKey) return
    setLoading(true)
    setError(null)

    const params = {}
    if (dateFrom) {
      try { params.commenceTimeFrom = new Date(dateFrom).toISOString() } catch { /* skip invalid */ }
    }
    if (dateTo) {
      try { params.commenceTimeTo = new Date(dateTo).toISOString() } catch { /* skip invalid */ }
    }

    const res = await fetchOddsEvents(sportKey, params)

    if (res.status === 'ok' && res.data) {
      setEvents(res.data)
      // Auto-expand all date groups
      const dates = new Set()
      res.data.forEach(e => {
        dates.add(new Date(e.commence_time).toLocaleDateString())
      })
      setExpandedDates(dates)
      setLastFetch(new Date())
    } else {
      setError(res.message || 'Failed to load events')
      setEvents([])
    }

    setLoading(false)
  }, [dateFrom, dateTo])

  // Auto-load when sport changes
  useEffect(() => {
    if (selectedSport) {
      fetchData(selectedSport)
    } else {
      setEvents([])
      setError(null)
      setLastFetch(null)
    }
  }, [selectedSport, fetchData])

  // Respond to quick-jumps from other widgets.
  useEffect(() => {
    if (initialSport && initialSport !== selectedSport) {
      setSelectedSport(initialSport)
      setSearchTerm('')
    }
  }, [initialSport, selectedSport])

  const handleSportChange = (e) => {
    const val = e.target.value
    setSelectedSport(val)
    setSearchTerm('')
  }

  const handleDateApply = () => {
    if (selectedSport) {
      fetchData(selectedSport)
    }
  }

  const clearDateFilters = () => {
    setDateFrom('')
    setDateTo('')
    // Re-fetch without date filters
    if (selectedSport) {
      // We need to wait for state to update, so just clear and let effect handle it
      // But dateFrom/dateTo are in fetchData deps, so clearing triggers re-fetch
    }
  }

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id)
      setCopyFeedback(id)
      setTimeout(() => setCopyFeedback(''), 1500)
    } catch { /* ignore */ }
  }

  const toggleDateExpand = (date) => {
    setExpandedDates(prev => {
      const next = new Set(prev)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }

  // Sort events by commence_time
  const sorted = [...events].sort(
    (a, b) => new Date(a.commence_time) - new Date(b.commence_time)
  )

  // Filter by search
  const filtered = searchTerm
    ? sorted.filter(e =>
        e.away_team?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.home_team?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : sorted

  // Group by date
  const byDate = {}
  filtered.forEach(e => {
    const d = new Date(e.commence_time).toLocaleDateString()
    if (!byDate[d]) byDate[d] = []
    byDate[d].push(e)
  })

  const selectedSportInfo = allSports.find(s => s.key === selectedSport)

  return (
    <div className="widget-card">
      <div
        className="widget-header ebw-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <Calendar size={16} className="widget-icon" />
        <span className="widget-title">Events Browser</span>
        {events.length > 0 && (
          <span className="ebw-summary">
            {filtered.length} event{filtered.length !== 1 ? 's' : ''}
          </span>
        )}
        <span className="status-badge ok">FREE</span>
        <ChevronRight
          size={16}
          className={`ebw-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* Sport dropdown */}
          <div className="ebw-controls">
            <div className="ebw-sport-row">
              <select
                className="ebw-select"
                value={selectedSport}
                onChange={handleSportChange}
                disabled={sportsLoading}
              >
                <option value="">
                  {sportsLoading ? 'Loading sports...' : 'Select a sport'}
                </option>
                {Object.keys(sportsByGroup).sort().map(group => (
                  <optgroup key={group} label={group}>
                    {sportsByGroup[group]
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map(s => (
                        <option key={s.key} value={s.key}>
                          {getSportIcon(s.key)} {s.title}
                        </option>
                      ))
                    }
                  </optgroup>
                ))}
              </select>

              {loading && <RefreshCw size={14} className="ebw-spinner" />}
            </div>

            {/* Date filter toggle */}
            <button
              className={`ebw-filter-toggle ${showDateFilters ? 'active' : ''}`}
              onClick={() => setShowDateFilters(v => !v)}
            >
              <Filter size={13} />
              <span>Date Filters</span>
              {(dateFrom || dateTo) && <span className="ebw-filter-dot" />}
            </button>

            {/* Collapsible date filters */}
            {showDateFilters && (
              <div className="ebw-date-filters">
                <div className="ebw-date-group">
                  <label className="ebw-date-label">From</label>
                  <input
                    type="datetime-local"
                    className="ebw-date-input"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="ebw-date-group">
                  <label className="ebw-date-label">To</label>
                  <input
                    type="datetime-local"
                    className="ebw-date-input"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="ebw-date-actions">
                  <button className="ebw-apply-btn" onClick={handleDateApply} disabled={!selectedSport}>
                    Apply
                  </button>
                  <button className="ebw-clear-btn" onClick={clearDateFilters}>
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {/* Summary + search */}
          {events.length > 0 && (
            <div className="ebw-summary-bar">
              <div className="ebw-stat">
                <span className="ebw-stat-value">{filtered.length}</span>
                <span className="ebw-stat-label">Events</span>
              </div>
              <div className="ebw-stat">
                <span className="ebw-stat-value">{Object.keys(byDate).length}</span>
                <span className="ebw-stat-label">Days</span>
              </div>
              <div className="ebw-results-search">
                <Search size={13} />
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ebw-results-search-input"
                />
              </div>
            </div>
          )}

          {/* Events grouped by date */}
          {Object.keys(byDate).length > 0 && (
            <div className="ebw-results">
              {Object.keys(byDate).map(date => {
                const dateEvents = byDate[date]
                const isOpen = expandedDates.has(date)

                return (
                  <div key={date} className="ebw-date-section">
                    <button
                      className="ebw-date-header"
                      onClick={() => toggleDateExpand(date)}
                    >
                      <ChevronRight
                        size={14}
                        className={`ebw-date-chevron ${isOpen ? 'rotated' : ''}`}
                      />
                      <span className="ebw-date-title">{date}</span>
                      <span className="ebw-date-count">{dateEvents.length} event{dateEvents.length !== 1 ? 's' : ''}</span>
                    </button>

                    {isOpen && (
                      <div className="ebw-date-content">
                        <div className="ebw-table-wrap">
                          <table className="ebw-table">
                            <thead>
                              <tr>
                                <th>Time</th>
                                <th>Away</th>
                                <th>Home</th>
                                <th className="ebw-col-id">Event ID</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dateEvents.map(event => {
                                const time = new Date(event.commence_time)
                                const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

                                return (
                                  <tr key={event.id}>
                                    <td className="ebw-time">{timeStr}</td>
                                    <td className="ebw-team"><TeamLogo teamName={event.away_team} sportKey={selectedSport} />{event.away_team || '-'}</td>
                                    <td className="ebw-team"><TeamLogo teamName={event.home_team} sportKey={selectedSport} />{event.home_team || '-'}</td>
                                    <td className="ebw-id ebw-col-id">
                                      <span className="ebw-id-text">{event.id.substring(0, 12)}...</span>
                                    </td>
                                    <td className="ebw-actions">
                                      {onGetEventOdds && (
                                        <button
                                          className="ebw-action-btn"
                                          onClick={() => onGetEventOdds(selectedSport, event.id)}
                                        >
                                          Odds
                                        </button>
                                      )}
                                      <button
                                        className="ebw-action-btn ebw-copy-btn"
                                        onClick={() => copyId(event.id)}
                                      >
                                        {copyFeedback === event.id ? (
                                          <><Check size={10} /> Copied</>
                                        ) : (
                                          <><Copy size={10} /> ID</>
                                        )}
                                      </button>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Empty states */}
          {!loading && events.length === 0 && lastFetch && (
            <div className="widget-body">
              <div className="widget-empty">No events found for {selectedSportInfo?.title || selectedSport}.</div>
            </div>
          )}

          {!loading && !selectedSport && (
            <div className="widget-body">
              <div className="widget-empty">Select a sport above to browse upcoming events.</div>
            </div>
          )}

          <div className="widget-footer">
            <span>{lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : 'Not loaded'}</span>
            <span>{selectedSportInfo?.title || 'No sport selected'}</span>
          </div>
        </>
      )}
    </div>
  )
}

export default EventsBrowserWidget
